// The choices the pickers offer above their calendars, the kit's WhenChoice and DeadlineChoice (TodoChoices.swift):
// the days they stand for, and which are offered on a day. Shared cases: cases/choices.json.

import { addDays, addMonths, dayFrom, dayParts, weekday } from "./days";
import type { Day, Weekday } from "./types";

/**
 * The coming day of the week (1 Sunday … 7 Saturday), a week ahead when it is that day today: the weekend (Saturday)
 * and next week (Monday) of When, and the deadline's Friday (Date.next(weekday:)).
 */
export function nextWeekday(today: Day, target: Weekday): Day {
  let day = addDays(today, 1);
  while (weekday(day) !== target) day = addDays(day, 1);
  return day;
}

/** The deadline's Friday: the coming one, next week's on a Friday. */
export function deadlineFriday(today: Day): Day {
  return nextWeekday(today, 6);
}

/** The last day of this month, or of the next on the month's last day (Date.endOfMonth(from:)). */
export function endOfMonth(today: Day): Day {
  const { year, month } = dayParts(today);
  const last = addDays(addMonths(dayFrom(year, month, 1), 1), -1);
  return last === today ? endOfMonth(addDays(today, 1)) : last;
}

export type WhenChoiceID = "today" | "tomorrow" | "weekend" | "nextWeek" | "anytime" | "someday";
export const WHEN_CHOICES: WhenChoiceID[] = ["today", "tomorrow", "weekend", "nextWeek", "anytime", "someday"];

/** The day a When choice stands for, or undefined for Anytime and Someday. */
export function whenChoiceDay(choice: WhenChoiceID, today: Day): Day | undefined {
  switch (choice) {
    case "today": return today;
    case "tomorrow": return addDays(today, 1);
    case "weekend": return nextWeekday(today, 7);
    case "nextWeek": return nextWeekday(today, 2);
    default: return undefined;
  }
}

/**
 * The When choices offered on a day: one that falls on the same day as a choice above it is left out, as the weekend
 * on a Friday and next week on a Sunday (Bastian, 2026-10-20).
 */
export function offeredWhenChoices(today: Day): WhenChoiceID[] {
  const days = new Set<Day>();
  return WHEN_CHOICES.filter((choice) => {
    const day = whenChoiceDay(choice, today);
    if (day === undefined) return true;
    if (days.has(day)) return false;
    days.add(day);
    return true;
  });
}

export type DeadlineChoiceID = "today" | "tomorrow" | "friday" | "inAWeek" | "endOfMonth";
export const DEADLINE_CHOICES: DeadlineChoiceID[] = ["today", "tomorrow", "friday", "inAWeek", "endOfMonth"];

/** The day a deadline choice sets. */
export function deadlineChoiceDay(choice: DeadlineChoiceID, today: Day): Day {
  switch (choice) {
    case "today": return today;
    case "tomorrow": return addDays(today, 1);
    case "friday": return deadlineFriday(today);
    case "inAWeek": return addDays(today, 7);
    case "endOfMonth": return endOfMonth(today);
  }
}

/** The deadline choices offered on a day, without one that falls on the same day as a choice above it. */
export function offeredDeadlineChoices(today: Day): DeadlineChoiceID[] {
  const days = new Set<Day>();
  return DEADLINE_CHOICES.filter((choice) => {
    const day = deadlineChoiceDay(choice, today);
    if (days.has(day)) return false;
    days.add(day);
    return true;
  });
}
